// Libs
import classNames from 'classnames/bind';
import { Button, Flex, Heading, Spinner, Table, Text, TextInput, View } from '@instructure/ui';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
// Components, Layouts, Pages
import { BasePagination, BaseSelect } from '~/components/common';
// Others
import { IExamForm } from '~/utils/mockData/common';
import { ExamStatusEnum } from '~/utils/enum/base';
import { defaultValuesForm, schema } from './helper';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { getStudents } from '~/thunks/user/userThunk';
import { IBodyCreateExam, IQueryGetExams } from '~/utils/interface/exam';
import { createExam, getListExams } from '~/thunks/exam/examThunk';
import { DEFAULT_LIMIT, DEFAULT_LIMIT_PAGE, DEFAULT_PAGE, TIMEOUT_ALERT_MILLISECOND } from '~/utils/constants/common';
import { getCoursesDetailOfStudent, getCoursesOfStudent, getModulesOfCourse } from '~/thunks/course/courseThunk';
import { IBaseSelectOption } from '~/utils/interface/common';
import { adminRoute } from '~/utils/routes/admin';
import i18n from '~/thirdParties/i18n';
// Styles, images, icons
import styles from './AdministrativeArea.module.scss';
import { icons } from '~/assets';
import { generateGuid } from '~/utils/helper/common';
import { IToast } from '~/utils/interface/toast';
import { toastActions } from '~/thunks/toast/toastSlice';
import { DocumentCourseReportPDF } from '~/pages/student/educationalProgram/documentPDF/DocumentCourseReportPdf';
import { pdf } from '@react-pdf/renderer';

type Props = {
  content?: string;
};

const cx = classNames.bind(styles);

const AdministrativeArea = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/home', 'common/base', 'admin/administrativeArea']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<IExamForm>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  const { students } = useAppSelector((state) => state.userState);
  const { exams, isLoadingCreateExam, isRefreshListExam, hasMore, isLoading, currentPage } = useAppSelector(
    (state) => state.examState
  );
  //#endregion Selector

  //#region Declare State
  const [courseOptions, setCourseOptions] = useState<IBaseSelectOption[]>([]);
  const [moduleOptions, setModuleOptions] = useState<IBaseSelectOption[]>([]);
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  const [isExportingReport, setIsExportingReport] = useState(false);

  const studentOptions =
    students?.responses.map((student) => ({
      value: student.id,
      label: student.fullName,
    })) ?? [];

  const statusOptions = [
    { value: ExamStatusEnum.PASSED, label: t('administrative_area_pass_status', { ns: 'admin/administrativeArea' }) },
    { value: ExamStatusEnum.FAILED, label: t('administrative_area_fail_status', { ns: 'admin/administrativeArea' }) },
  ];
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetStudents();
    handleGetExams();
  }, []);

  useEffect(() => {
    if (isRefreshListExam) {
      handleGetExams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshListExam]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    handleGetExams({
      limit: DEFAULT_LIMIT_PAGE,
      page: newPage,
    });
  };

  const handleGetExams = async (query?: IQueryGetExams) => {
    await dispatch(
      getListExams(
        query ?? {
          limit: DEFAULT_LIMIT_PAGE,
          page: page,
        }
      )
    ).unwrap();
  };

  const handleGetStudents = async () => {
    await dispatch(
      getStudents({
        limit: DEFAULT_LIMIT,
      })
    ).unwrap();
  };

  const handleGetCoursesOfStudent = async (studentId: string) => {
    await dispatch(getCoursesOfStudent({ studentId }))
      .unwrap()
      .then((res) => {
        if (!res.data) return;

        setCourseOptions(
          res.data.map((item) => ({
            value: item.id,
            label: item.name,
          }))
        );
      })
      .catch((error) => {})
      .finally(() => {});
  };

  const handleGetModulesOfCourse = async (courseId: string) => {
    await dispatch(getModulesOfCourse({ courseId }))
      .unwrap()
      .then((res) => {
        if (!res.data) return;

        setModuleOptions(
          res.data.map((item) => ({
            value: item.id,
            label: item.name,
          }))
        );
      })
      .catch((error) => {})
      .finally(() => {});
  };

  const handleBlurPointInput = (event: React.FocusEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();
    if (!value) {
      setValue('pointValue', '');
      return;
    }

    const numberValue = parseFloat(value);
    setValue('pointValue', isFinite(numberValue) ? numberValue.toString() : '');
  };

  const handleBlurCreditInput = (event: React.FocusEvent<HTMLInputElement>) => {
    const value = event.target.value.trim();
    if (!value) {
      setValue('credits', '');
      return;
    }

    const numberValue = parseFloat(value);
    setValue('credits', isFinite(numberValue) ? numberValue.toString() : '');
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleCreateExam = async (payload: IBodyCreateExam) => {
    await dispatch(createExam(payload))
      .unwrap()
      .then((res) => {
        reset();
      })
      .catch((error) => {})
      .finally(() => {});
  };

  const handleSubmitForm = (data: IExamForm) => {
    if (!data) return;

    const payload: IBodyCreateExam = {
      studentId: data.studentId,
      studentName: data.studentName,
      courseId: data.courseId,
      courseName: data.courseName,
      moduleId: data.moduleId,
      moduleName: data.moduleName,
      status: data.status,
      credits: +data.credits,
      pointValue: +data.pointValue,
      value: data.value,
    };
    handleCreateExam(payload);
  };

  const handleSetTotalExam = () => {
    navigate(`${adminRoute.base}${adminRoute.courses}`);
  };

  const handleDownloadCourseReportPDF = async () => {
    if (isExportingReport) return;

    const selectedStudentId = control._formValues?.studentId;
    if (!selectedStudentId) {
      const messageId = generateGuid();
      const toast: IToast = {
        id: messageId,
        message: t('admin_please_select_student', { ns: 'common/base' }),
        type: 'error',
      };
      dispatch(toastActions.addToast(toast));
      setTimeout(() => {
        dispatch(toastActions.removeToast(messageId));
      }, TIMEOUT_ALERT_MILLISECOND);
      return;
    }

    setIsExportingReport(true);
    try {
      const res = await dispatch(getCoursesDetailOfStudent(selectedStudentId)).unwrap();
      const data = res.data;

      const pdfData = {
        student: {
          id: data.student.id,
          name: data.student.name,
          lastLogin: data.student.lastLogin,
          loginAt: data.student.loginAt,
        },
        courses: data.courses ?? [],
      };

      if (!pdfData.courses.length) {
        const messageId = generateGuid();
        const toast: IToast = {
          id: messageId,
          message: t('common_error_message', { ns: 'common/base' }),
          type: 'error',
        };
        dispatch(toastActions.addToast(toast));
        setTimeout(() => {
          dispatch(toastActions.removeToast(messageId));
        }, TIMEOUT_ALERT_MILLISECOND);
        return;
      }

      await i18n.loadNamespaces(['common/course-details-report']);
      const blob = await pdf(<DocumentCourseReportPDF data={pdfData} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `course-report-${pdfData.student.name || 'student'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      const messageId = generateGuid();
      const toast: IToast = {
        id: messageId,
        message: t('common_error_message', { ns: 'common/base' }),
        type: 'error',
      };
      dispatch(toastActions.addToast(toast));
      setTimeout(() => {
        dispatch(toastActions.removeToast(messageId));
      }, TIMEOUT_ALERT_MILLISECOND);
    } finally {
      setIsExportingReport(false);
    }
  };

  //#endregion Handle Function

  return (
    <div className={cx('viewPort')}>
      <div className={cx('backContainer')} onClick={handleBack}>
        <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} className={cx('iconBack')} />
        <Text variant='content'>{t('common_back_button', { ns: 'common/base' })}</Text>
      </div>

      <div className={cx('container')}>
        <View as='div' className={cx('administrativeHeader')} width={'100%'}>
          <View as='div' className={cx('administrativeTitle')}>
            <Heading level='h3'>{t('administrative_area_title', { ns: 'admin/administrativeArea' })}</Heading>
            <img
              className={cx('iconArea')}
              src={icons.commonIconExamArea}
              alt={t('common_alt_img', { ns: 'common/base' })}
            />
          </View>

          <Flex gap='small' alignItems='center'>
            <Button color='primary' type='button' disabled={isExportingReport} onClick={handleDownloadCourseReportPDF}>
              <Flex justifyItems='center' alignItems='center' gap='small'>
                <span>{t('administrative_area_download_exam_report_pdf', { ns: 'admin/administrativeArea' })}</span>
                {isExportingReport && <Spinner size='x-small' />}
              </Flex>
            </Button>
            <Button color='primary' type='button' onClick={handleSetTotalExam}>
              {t('administrative_area_set_total_exams_button_label', { ns: 'admin/administrativeArea' })}
            </Button>
          </Flex>
        </View>

        <View as='div' className={cx('administrativeForm')} margin='medium 0' width={'100%'}>
          <form onSubmit={handleSubmit(handleSubmitForm)}>
            <Flex direction={'column'} gap='medium' width={'100%'}>
              <View as='div' className={cx('administrativeFirstContainer')}>
                <View as='div' className={cx('administrativeSelect')}>
                  <Controller
                    name='studentId'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('administrative_area_select_student_label', { ns: 'admin/administrativeArea' })}
                        </label>
                        <BaseSelect
                          value={value}
                          options={studentOptions}
                          onChange={(e) => {
                            onChange(e.value);
                            setValue('studentName', e.label);
                            handleGetCoursesOfStudent(e.value);
                          }}
                        />
                        {errors?.studentId?.message && (
                          <View className={cx('errorMessage')}>
                            <Text variant='legend' color='danger'>
                              {errors?.studentId?.message}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  />
                </View>

                <View as='div' className={cx('administrativeSelect')}>
                  <Controller
                    name='courseId'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('administrative_area_select_course_label', { ns: 'admin/administrativeArea' })}
                        </label>
                        <BaseSelect
                          value={value}
                          options={courseOptions}
                          onChange={(e) => {
                            onChange(e.value);
                            setValue('courseName', e.label);
                            handleGetModulesOfCourse(e.value);
                          }}
                        />
                        {errors?.courseId?.message && (
                          <View className={cx('errorMessage')}>
                            <Text variant='legend' color='danger'>
                              {errors?.courseId?.message}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  />
                </View>

                <View as='div' className={cx('administrativeSelect')}>
                  <Controller
                    name='moduleId'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('administrative_area_select_module_label', { ns: 'admin/administrativeArea' })}
                        </label>
                        <BaseSelect
                          value={value}
                          options={moduleOptions}
                          onChange={(e) => {
                            onChange(e.value);
                            setValue('moduleName', e.label);
                          }}
                        />
                        {errors?.moduleId?.message && (
                          <View className={cx('errorMessage')}>
                            <Text variant='legend' color='danger'>
                              {errors?.moduleId?.message}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  />
                </View>

                <View as='div' className={cx('administrativeSelect')}>
                  <Controller
                    name='status'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('administrative_area_insert_exam_label', { ns: 'admin/administrativeArea' })}
                        </label>
                        <BaseSelect value={value} options={statusOptions} onChange={(e) => onChange(e.value)} />
                        {errors?.status?.message && (
                          <View className={cx('errorMessage')}>
                            <Text variant='legend' color='danger'>
                              {errors?.status?.message}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  />
                </View>
              </View>

              <View as='div' className={cx('administrativeSecondContainer')}>
                <View as='div' className={cx('administrativeInput')}>
                  <Controller
                    name='pointValue'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('administrative_area_insert_point_label', { ns: 'admin/administrativeArea' })}
                        </label>
                        <TextInput
                          value={value}
                          onBlur={handleBlurPointInput}
                          onChange={(e) => onChange(e.target.value)}
                        />
                        {errors?.pointValue?.message && (
                          <View className={cx('errorMessage')}>
                            <Text variant='legend' color='danger'>
                              {errors?.pointValue?.message}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  />
                </View>

                <View as='div' className={cx('administrativeInput')}>
                  <Controller
                    name='value'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('administrative_area_insert_value_label', { ns: 'admin/administrativeArea' })}
                        </label>
                        <TextInput value={value} onChange={(e) => onChange(e.target.value)} />
                        {errors?.value?.message && (
                          <View className={cx('errorMessage')}>
                            <Text variant='legend' color='danger'>
                              {errors?.value?.message}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  />
                </View>

                <View as='div' className={cx('administrativeInput')}>
                  <Controller
                    name='credits'
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <>
                        <label className={cx('administrativeLabel')}>
                          {t('administrative_area_insert_credit_label', { ns: 'admin/administrativeArea' })}
                        </label>
                        <TextInput
                          value={value}
                          onBlur={handleBlurCreditInput}
                          onChange={(e) => onChange(e.target.value)}
                        />
                        {errors?.credits?.message && (
                          <View className={cx('errorMessage')}>
                            <Text variant='legend' color='danger'>
                              {errors?.credits?.message}
                            </Text>
                          </View>
                        )}
                      </>
                    )}
                  />
                </View>
              </View>
            </Flex>

            <Flex justifyItems='center' margin='medium 0 0 0'>
              <View as='div' className={cx('administrativeButton')}>
                <Button
                  color='primary'
                  type='submit'
                  disabled={isLoadingCreateExam}
                  renderIcon={isLoadingCreateExam && <Spinner size='x-small' />}
                >
                  {t('administrative_area_add_button_label', { ns: 'admin/administrativeArea' })}
                </Button>
              </View>
            </Flex>
          </form>
        </View>

        <View className={cx('examTableDesktop')}>
          <Table layout='auto'>
            <Table.Head></Table.Head>
            <Table.Body>
              {exams.map((data, index) => (
                <Table.Row key={index}>
                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>{data.studentName}</span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>{data.courseName}</span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>{data.moduleName}</span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>{data.pointValue}</span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>{data.value}</span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className={cx('administrativeStatus')}>
                      <img
                        src={
                          data.status === ExamStatusEnum.FAILED
                            ? icons.commonIconStatusFailed
                            : icons.commonIconChangeStatusActive
                        }
                        alt={t('common_alt_img', { ns: 'common/base' })}
                        className={cx('iconStatus')}
                      />
                      <span>
                        {data.status === ExamStatusEnum.FAILED
                          ? t('administrative_area_failed_status', { ns: 'admin/administrativeArea' })
                          : t('administrative_area_passed_status', { ns: 'admin/administrativeArea' })}
                      </span>
                    </div>
                  </Table.Cell>

                  <Table.Cell>
                    <div className={cx('administrativeData')}>
                      <span>
                        {t('administrative_area_credit_value', {
                          ns: 'admin/administrativeArea',
                          quantity: data.credits,
                        })}
                      </span>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>

          <BasePagination currentPage={page} hasMore={hasMore} onChange={handleChangePage} />
        </View>

        <View className={cx('examTableMobile')}>
          {exams.map((data, index) => (
            <View key={index} as='div' className={cx('card')}>
              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_select_student_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>{data.studentName}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_select_course_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>{data.courseName}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_select_module_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>{data.moduleName}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_table_point_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>{data.pointValue}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_table_value_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>{data.value}</span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_table_credit_label', { ns: 'admin/administrativeArea' })}
                </span>
                <span>
                  {t('administrative_area_credit_value', {
                    ns: 'admin/administrativeArea',
                    quantity: data.credits,
                  })}
                </span>
              </View>

              <View as='div' className={cx('cardItem')}>
                <span className={cx('cardLabel')}>
                  {t('administrative_area_table_status_label', { ns: 'admin/administrativeArea' })}
                </span>

                <View as='div' className={cx('cardItem', 'status')}>
                  <img
                    src={
                      data.status === ExamStatusEnum.FAILED
                        ? icons.commonIconStatusFailed
                        : icons.commonIconChangeStatusActive
                    }
                    alt='Status Icon'
                    className={cx('iconStatus')}
                  />
                  <span>
                    {data.status === ExamStatusEnum.FAILED
                      ? t('administrative_area_failed_status', { ns: 'admin/administrativeArea' })
                      : t('administrative_area_passed_status', { ns: 'admin/administrativeArea' })}
                  </span>
                </View>
              </View>
            </View>
          ))}

          <BasePagination currentPage={page} hasMore={hasMore} onChange={handleChangePage} />
        </View>
      </div>
    </div>
  );
};

export default AdministrativeArea;
